import Model, { attr, belongsTo } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { formatDate } from 'uplisting-frontend/helpers/date-format';

export enum MessageStatus {
  pending = 'pending',
  sent = 'sent',
  delivered = 'delivered',
  failed = 'failed',
  received = 'received',
}

export interface IMessageAttachment {
  name: string;
  url: string;
  image: boolean;
}

export enum ContextType {
  subscriptionRequest = 'subscription_request',
  subscriptionApproval = 'subscription_approval',
  subscriptionRejection = 'subscription_rejection',
  subscriptionHelp = 'subscription_help',
  conversation = 'conversation',
  automation = 'automation',
}

export default class MessageModel extends Model {
  @attr('string') name!: string;
  @attr('string') content!: string;
  @attr('string') image?: string | null;
  @attr() attachments?: IMessageAttachment[];
  @attr('string') mediaUrl?: string | null;
  @attr('string') mediaType?: string | null;
  @attr('string') type!: string;
  @attr('string') thumbnailUrl!: string;
  @attr('boolean') inbound!: boolean;
  @attr('date') createdAt!: Date;
  @attr('string') failureReason?: string | null;
  @attr('string') status!: MessageStatus;
  @attr('string') context!: ContextType;
  @attr('string') userName?: string | null;
  @attr('string') language?: string | null;
  @attr('string') translatedContent?: string | null;

  @cached
  get imageAttachments(): IMessageAttachment[] {
    const images = (this.attachments ?? []).filter(
      (attachment) => attachment.image,
    );

    if (images.length) {
      return images;
    }

    if (!this.image) {
      return [];
    }

    return [{ name: '', url: this.image, image: true }];
  }

  @cached
  get fileAttachments(): IMessageAttachment[] {
    return (this.attachments ?? []).filter((attachment) => !attachment.image);
  }

  @cached
  get hasTranslation(): boolean {
    return !!this.translatedContent && this.translatedContent !== this.content;
  }

  @cached
  get createdAtFormatted(): string {
    return formatDate(this.createdAt, 'MMMM do yyyy - h:mm aaa');
  }

  @cached
  get isSms(): boolean {
    return this.type === 'sms';
  }

  @cached
  get isPending(): boolean {
    return this.status === MessageStatus.pending;
  }

  @cached
  get isFailed(): boolean {
    return this.status === MessageStatus.failed;
  }

  @cached
  get isProcessing(): boolean {
    return (
      this.status === MessageStatus.sent ||
      this.status === MessageStatus.pending
    );
  }

  @cached
  get isConversation(): boolean {
    return this.context === ContextType.conversation;
  }

  @belongsTo('booking', { inverse: 'messages', async: false })
  booking!: ModelRegistry['booking'];

  @belongsTo('booking-request', { inverse: 'messages', async: false })
  bookingRequest!: ModelRegistry['booking-request'];

  @belongsTo('enquiry', { inverse: 'messages', async: false })
  enquiry!: ModelRegistry['enquiry'];
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    message: MessageModel;
  }
}
